import {
  AssigneeDetails,
  OrderHistory,
  OrderHistoryStatus,
  VendorDelivery,
} from '@buncha/B2B/hooks/type';
import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import Call from '@buncha/components/core/icon/Call';
// import ContactShopperButton from '../contactShopperButton/ContactShopperButton';
import If from '@buncha/components/conditional/If';
import {Linking} from 'react-native';
import {Modules} from '@buncha/utils/types';
import Notes from '@buncha/components/core/icon/Notes';
import {Run} from '@buncha/AboutShift/types';
// import ShopperProfileImage from '../shopperProfileImage/ShopperProfileImage';
import {Skeleton} from 'native-base';
import StepHeader from '../stepHeader/StepHeader';
import {Touchable} from '@buncha/components/core/touchable';
// import font from '@buncha/appStyles/style/font';
import moment from 'moment';
import {styles} from './styles';
import {useFetchGetData} from '@buncha/config/hooks/axios';
import {useGetLocale} from '@buncha/localization/hooks';

interface DriverStepProps {
  isActive: boolean;
  assigneeDetails?: AssigneeDetails;
  externalOrderId: string;
  run?: Run;
  orderHistory?: OrderHistory;
  setScreenTitle?: React.Dispatch<React.SetStateAction<string>>;
  stateIndex?: number;
  vendorDelivery: VendorDelivery;
  deliveryNotes?: string;
  driverDetails?: AssigneeDetails;
}

const DriverStep: React.FC<DriverStepProps> = props => {
  const {
    isActive,
    // assigneeDetails,
    externalOrderId,
    run,
    orderHistory,
    setScreenTitle,
    stateIndex,
    vendorDelivery,
    deliveryNotes,
    driverDetails,
  } = props;

  const [getTrackingData, trackingData, trackingDataLoading] = useFetchGetData<{
    data: {trackingLink: string};
  }>();
  const {title, body} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.OrderInfo) ?? {};
  const theme = useColorThemeType();
  const muliFont = useFontTheme();
  const link = trackingData?.data?.trackingLink;
  const delayedValue = run?.delayedDropOffEndTime;

  const timeStamp = orderHistory?.createdAt.length
    ? moment(orderHistory?.createdAt).format('hh:mm a')
    : '';

  const callDriverHandler = useCallback(() => {
    if (driverDetails?.phone) Linking.openURL(`tel:${driverDetails.phone}`);
  }, [driverDetails?.phone]);

  const titleText = useMemo(() => {
    if (vendorDelivery?.code) return title?.outForDeliveryWithoutRefrigerated;

    return title?.outForDeliveryWithoutRefrigerated;
  }, [title?.outForDeliveryWithoutRefrigerated, vendorDelivery?.code]);

  useEffect(() => {
    if (isActive && setScreenTitle) setScreenTitle(titleText);
  }, [isActive, setScreenTitle, titleText, vendorDelivery]);

  useEffect(() => {
    if (!externalOrderId.length || !isActive) return;
    getTrackingData(
      `/api/v1/customer/order/${externalOrderId}/tracking-url`,
      {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOrderId, isActive]);

  const iFrameStyles = useMemo(() => {
    if (orderHistory?.status !== OrderHistoryStatus.inTransit)
      return styles.iFrameContainer;
    return {
      ...styles.iFrameContainer,
      ...styles.iFrameContainerBeforeInTransit,
    };
  }, [orderHistory?.status]);

  return (
    <Box>
      <StepHeader textTitle={titleText} timeString={timeStamp} />
      <If condition={isActive}>
        <Box style={styles.container}>
          <CustomText
            style={[
              styles.messageText,
              {
                color: theme?.text.blue[300],
                fontFamily: muliFont[400].normal,
              },
            ]}>
            {titleText}
          </CustomText>
          <If condition={trackingDataLoading}>
            <Skeleton style={styles.skeletonBox} />
          </If>
          <If condition={!trackingDataLoading}>
            <If condition={!vendorDelivery?.code}>
              <Box
                style={[
                  styles.orderBox,
                  {
                    borderColor: theme?.background.gray[200],
                    height: link?.length ? 500 : 110,
                  },
                ]}>
                <iframe
                  key={stateIndex}
                  src={link ?? 'https://onf.lt/3c839d4d82'}
                  width="100%"
                  style={iFrameStyles as React.CSSProperties}
                />
              </Box>
              <If
                condition={
                  orderHistory?.status === OrderHistoryStatus.inTransit
                }>
                <H6Text style={styles.textStyle}>{body?.inTransitText}</H6Text>
              </If>
            </If>
          </If>
          <If condition={vendorDelivery?.code}>
            <Box
              style={[
                styles.vendorCard,
                {borderColor: theme?.background.gray[200]},
              ]}>
              <If condition={driverDetails?.name}>
                <HBox style={styles.driverRow}>
                  <HBox style={styles.driverInfoRow}>
                    <Box
                      style={[
                        styles.driverAvatar,
                        {backgroundColor: theme?.background.blue[200]},
                      ]}>
                      <CustomText
                        style={[
                          styles.driverAvatarText,
                          {
                            color: theme?.text.blue[300],
                            fontFamily: muliFont[700].normal,
                          },
                        ]}>
                        {driverDetails?.name?.charAt(0)?.toUpperCase()}
                      </CustomText>
                    </Box>
                    <Box style={styles.driverTextColumn}>
                      <CustomText
                        style={[
                          styles.driverNameTextNew,
                          {
                            color: theme?.text.blue[300],
                            fontFamily: muliFont[700].normal,
                          },
                        ]}>
                        {driverDetails?.name}
                      </CustomText>
                      <If condition={driverDetails?.phone}>
                        <CustomText
                          style={[
                            styles.driverPhoneTextNew,
                            {
                              color: theme?.text.gray[100],
                              fontFamily: muliFont[400].normal,
                            },
                          ]}>
                          {driverDetails?.phone}
                        </CustomText>
                      </If>
                    </Box>
                  </HBox>
                  <If condition={driverDetails?.phone}>
                    <Touchable
                      onPress={callDriverHandler}
                      style={[
                        styles.callButton,
                        {
                          backgroundColor: theme?.background.blue[100],
                        },
                      ]}>
                      <Call
                        width={16}
                        height={16}
                        viewBox="0 0 25 20"
                        fill={theme?.icon?.white[100]}
                      />
                    </Touchable>
                  </If>
                </HBox>
              </If>
              <If condition={deliveryNotes}>
                <Box
                  style={[
                    styles.deliveryInstructionsContainer,
                    {borderTopColor: theme?.background.gray[200]},
                  ]}>
                  <HBox style={styles.deliveryInstructionsHeader}>
                    <Notes
                      width={13}
                      height={13}
                      viewBox="0 0 18 18"
                      fill={theme?.icon?.gray[100]}
                    />
                    <CustomText
                      style={[
                        styles.deliveryInstructionsLabel,
                        {
                          color: theme?.text.gray[100],
                          fontFamily: muliFont[600].normal,
                        },
                      ]}>
                      {body?.deliveryNote || 'Delivery Instructions'}
                    </CustomText>
                  </HBox>
                  <CustomText
                    style={[
                      styles.deliveryInstructionsText,
                      {
                        color: theme?.text.blue[300],
                        fontFamily: muliFont[400].normal,
                      },
                    ]}>
                    {deliveryNotes}
                  </CustomText>
                </Box>
              </If>
            </Box>
          </If>
          {/* <HBox style={styles.hboxWrapper}>
            <ShopperProfileImage
              imageWidth={65}
              imageHeight={65}
              buttonWidth={80}
              textStyle={{...font.FontSize.fontP10}}
              assigneeId={assigneeDetails?.id ?? ''}
              isShopper={false}
              phoneNumber={assigneeDetails?.phone ?? ''}
              imageUrl={assigneeDetails?.imageUrl}
            />
            <Box style={styles.textFlex}>
              <H5Text
                style={[
                  styles.driverNameText,
                  {
                    color: theme?.text.blue[300],
                    fontFamily: muliFont[700].normal,
                  },
                ]}>
                {assigneeDetails?.name}
                <H5Text
                  style={[
                    styles.driverSubText,
                    {
                      color: theme?.text.blue[300],
                      fontFamily: muliFont[400].normal,
                    },
                  ]}>
                  {body?.onTheWay}
                </H5Text>
              </H5Text>
              <CustomText
                style={[
                  styles.contactText,
                  {
                    color: theme?.text.blue[300],
                    fontFamily: muliFont[400].normal,
                  },
                ]}>
                {body?.contactYourDriver}
              </CustomText>
            </Box>
          </HBox> */}
          {/* <ContactShopperButton
            isShopper={false}
            phoneNumber={assigneeDetails?.phone}
          /> */}
          <Box
            style={[
              styles.etaContainer,
              {
                backgroundColor: theme?.background.gray[200],
                borderColor: theme?.background.gray[200],
              },
            ]}>
            <If condition={delayedValue?.length && !vendorDelivery?.code}>
              <H6Text
                style={[
                  styles.delayedText,
                  {
                    color: theme?.text.gray[100],
                    fontFamily: muliFont[600].normal,
                  },
                ]}>
                {body?.orderDelayed}
              </H6Text>
            </If>
            <HBox style={styles.etaHBox}>
              <H6Text style={{color: theme?.text.blue[300]}}>
                {vendorDelivery?.code
                  ? body?.deliveryWindow
                  : delayedValue?.length
                  ? body?.newETA
                  : body?.deliveryWindow}
              </H6Text>
              <H6Text style={{color: theme?.text.blue[300]}}>
                {vendorDelivery?.dropoffEtaEstimated ||
                  `${run?.dropOffStartTime} - ${
                    delayedValue?.length ? delayedValue : run?.dropOffEndTime
                  }`}
              </H6Text>
            </HBox>
          </Box>
        </Box>
      </If>
    </Box>
  );
};

export default React.memo(DriverStep);
